<script setup>
import { computed, onMounted } from 'vue'
import { useTheme } from 'vuetify'
import { generateHTML } from '@tiptap/html'
import { useTiptapStore } from '@/tiptap/hooks/use-store'
import '@/tiptap/styles/index.scss'
import { sanitizeContent } from '@/utils/sanitizeHelper'
import { TextStyle } from '@tiptap/extension-text-style'
import Color from '@/tiptap/extensions/color'
import FontFamily from '@/tiptap/extensions/font-family'
import FontSize from '@/tiptap/extensions/font-size'
import FontWeight from '@/tiptap/extensions/font-weight'
import LetterSpacing from '@/tiptap/extensions/letter-spacing'
import Video from '@/tiptap/extensions/video'

// Keep in sync with extensions/video.js (yt/shorts/nocookie/vimeo/drive)
function linkConvert(src) {
  if (!src) return src
  let out = src
  // youtu.be -> youtube watch
  out = out.replace('https://youtu.be/', 'https://www.youtube.com/watch?v=')
  // watch?v= -> embed/
  out = out.replace('watch?v=', 'embed/')
  // shorts -> embed
  out = out.replace('https://youtube.com/shorts/', 'https://www.youtube.com/embed/')
  out = out.replace('https://www.youtube.com/shorts/', 'https://www.youtube.com/embed/')
  // prefer nocookie domain for embeds
  out = out.replace('https://www.youtube.com/embed/', 'https://www.youtube-nocookie.com/embed/')
  // vimeo -> player
  out = out.replace('https://vimeo.com/', 'https://player.vimeo.com/video/')
  // google drive view -> preview
  if (out.includes('drive.google.com')) out = out.replace('/view', '/preview')
  // strip query params like ?feature=share
  return out.replace(/\?.*$/, '')
}

function isTipTapDoc(obj) {
  return obj && typeof obj === 'object' && obj.type === 'doc';
}
function safeDoc(val) {
  // Accept either a valid TipTap doc JSON or fall back to an empty doc
  if (isTipTapDoc(val)) return val;
  // Some legacy payloads may wrap under { content_json: {...} } or similar
  const maybeDoc = val && typeof val === 'object' && val.content ? { type: 'doc', content: val.content } : null;
  if (isTipTapDoc(maybeDoc)) return maybeDoc;
  return { type: 'doc', content: [] };
}

/**
 * Lightweight viewer for TipTap content (JS version)
 * - Works with plain HTML strings or TipTap JSON (generateHTML)
 * - Applies the same markdown theme class used by the editor
 */

const props = defineProps({
  value: { type: [String, Object], default: '' },   // HTML string or TipTap JSON
  dark: { type: Boolean, default: undefined },      // force dark/light; otherwise follow Vuetify theme
  dense: { type: Boolean, default: false },
  xss: { type: [Boolean, Array], default: true },   // true | false | [extra allowed tags]
  xssOptions: { type: Object, default: () => ({}) }, // optional xss whitelist overrides (currently unused; kept for API parity)
  extensions: { type: Array, default: () => [] },   // TipTap extensions for generateHTML when value is JSON
})

const theme = useTheme()
const { state } = useTiptapStore()
const SHARED_HANDBOOK_CSS = [
  {
    href: '/static/core/css/handbook_content.css?v=20260313-1',
    id: 'sg-handbook-content-styles-link',
    query: 'link[data-sg-handbook-content-styles="1"]',
    datasetKey: 'sgHandbookContentStyles',
  },
  {
    href: '/static/core/css/handbook_diff.css?v=20260313-1',
    id: 'sg-handbook-diff-styles-link',
    query: 'link[data-sg-handbook-diff-styles="1"]',
    datasetKey: 'sgHandbookDiffStyles',
  },
]

const SHARED_HANDBOOK_FONT_PRELOADS = [
  '/static/core/fonts/Archivo-400.ttf',
  '/static/core/fonts/Archivo-600.ttf',
  '/static/core/fonts/Archivo-700.ttf',
]

function ensureSharedHandbookStyles() {
  if (typeof document === 'undefined') return
  SHARED_HANDBOOK_FONT_PRELOADS.forEach((href) => {
    if (document.querySelector(`link[rel="preload"][href="${href}"]`)) return

    const link = document.createElement('link')
    link.rel = 'preload'
    link.as = 'font'
    link.type = 'font/ttf'
    link.href = href
    link.crossOrigin = 'anonymous'
    document.head.appendChild(link)
  })

  SHARED_HANDBOOK_CSS.forEach((style) => {
    if (document.getElementById(style.id)) return
    if (document.querySelector(style.query)) return
    if (document.querySelector(`link[href="${style.href}"]`)) return

    const link = document.createElement('link')
    link.id = style.id
    link.rel = 'stylesheet'
    link.href = style.href
    link.dataset[style.datasetKey] = '1'
    document.head.appendChild(link)
  })
}

function uniqueByName(list) {
  const m = new Map()
  list.forEach(ext => {
    const name = ext?.name || ext?.config?.name
    if (!name) return
    m.set(name, ext) // last wins
  })
  return [...m.values()]
}

const stripNames = new Set(['textStyle', 'color', 'fontFamily', 'fontSize', 'fontWeight', 'letterSpacing'])
function stripStyleExts(arr = []) {
  return (arr || []).filter(e => !stripNames.has(e?.name || e?.config?.name))
}

const cleanedStateExts = computed(() => stripStyleExts(state.extensions))
const cleanedPropExts  = computed(() => stripStyleExts(props.extensions))

const renderExtensions = computed(() =>
  uniqueByName([
    ...cleanedStateExts.value,
    ...cleanedPropExts.value,
    Video,
    // ensure single instances of these come last
    TextStyle,
    Color,
    FontFamily,
    FontSize,
    FontWeight,
    LetterSpacing,
  ])
)

const isDark = computed(() => {
  if (typeof props.dark === 'boolean') return props.dark
  const current = theme?.current?.value?.dark
  return typeof current === 'boolean' ? current : false
})

const viewerClass = computed(() => ({
  __dark: isDark.value,
  dense: !!props.dense,
  view: true,
  'markdown-theme-github': true,
}))

const htmlValue = computed(() => {
  // String: treat as trusted HTML/markdown already rendered by server
  if (typeof props.value === 'string') return props.value || '';
  // Object: TipTap JSON -> HTML
  try {
    // Use EXACTLY the same extensions as the editor so Image width/height are rendered
    return generateHTML(safeDoc(props.value || {}), renderExtensions.value);
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      // Common cause: legacy JSON with unknown node type for current extension set
      console.warn('[VuetifyViewer] generateHTML failed, falling back to empty doc:', e);
    }
    return generateHTML(safeDoc({}), renderExtensions.value);
  }
})

const cleanValue = computed(() => {
  let html = (htmlValue.value || '')

  // Rewrite iframe/src and anchor/href to our canonical embed forms
  html = html.replace(/(<iframe[^>]*\ssrc=["'])([^"']+)(["'][^>]*>)/gi, (_, p1, url, p3) => {
    return p1 + linkConvert(url) + p3
  })
  html = html.replace(/(<a[^>]*\shref=["'])([^"']+)(["'][^>]*>)/gi, (_, p1, url, p3) => {
    return p1 + linkConvert(url) + p3
  })

  if (props.xss === false) return html

  // Allow iframes from trusted hosts and necessary attrs so YouTube/Vimeo survive sanitization
  const sanitized = sanitizeContent(html, {
    addTags: ['iframe'],
    addAttr: [
      'allow', 'allowfullscreen', 'referrerpolicy', 'frameborder',
      'width', 'height', 'style', 'class', 'loading'
    ],
    addIframeHostnames: [
      'www.youtube-nocookie.com',
      'www.youtube.com',
      'player.vimeo.com',
      'i.ytimg.com',
      's.ytimg.com',
      '*.googlevideo.com'
    ]
  })
  return sanitized
})

const viewerKey = computed(() => {
  // Key by a cheap fingerprint of the normalized content to avoid stale DOM between modal reopens
  try {
    if (typeof props.value === 'string') return 's:' + String(props.value).length + ':' + String(props.value).slice(0, 64);
    const json = JSON.stringify(safeDoc(props.value || {}));
    return 'j:' + json.length + ':' + json.slice(0, 64);
  } catch {
    return 'k:' + Date.now();
  }
});

onMounted(() => {
  ensureSharedHandbookStyles()
})
</script>

<template>
  <div class="vuetify-pro-tiptap-editor__content" :class="viewerClass" :style="{ width: '100%' }">
    <slot name="before"></slot>
    <!-- eslint-disable-next-line vue/no-v-html -->
    <div :key="viewerKey" class="content doc-wrap markdown-theme-github" v-html="cleanValue"></div>
    <slot name="after"></slot>
  </div>
</template>

<style scoped>
/* Respect explicit width/height emitted by the Image extension */
.vuetify-pro-tiptap-editor__content :deep(.content.doc-wrap img) {
  max-width: 100%;
  height: auto;
}
.vuetify-pro-tiptap-editor__content :deep(.content.doc-wrap iframe) {
  display: block;
  max-width: 100%;
  border: 0;
}
.vuetify-pro-tiptap-editor__content :deep(.content.doc-wrap .iframe-wrapper) {
  display: flex;
  justify-content: center; /* honor extension's centering */
}

</style>
